import Konva from 'konva'
import {getCustomShapeWorker} from '../../../../../webworkers/helpers/customShape'
import {downscaleImageData} from '../../../../../features/custom-shapes/fragment-detection/helpers/downscaleImageData'
import {resizeImageData} from '../../../../../features/custom-shapes/fragment-detection/helpers/resizeImageData'
import {FRAGMENT_DETECTION} from '../../../../../constant/fragmentDetection'

/** Stroke color for silhouette: black only (RGBA array for worker). */
const SILHOUETTE_STROKE_BLACK = [0, 0, 0, 255]

/**
 * Alpha filter factory for silhouette mask
 * @param {Object} options - { min, max } alpha range
 * @returns {Function} filter function for Konva
 */
export function alphaFilterFactory(options = {}) {
    const {min = 254, max = 255} = options
    return function alphaThreshold(imageData) {
        for (let i = 0; i < imageData.data.length; i += 4) {
            if (imageData.data[i + 3] < min || imageData.data[i + 3] > max) {
                imageData.data[i + 3] = 0
            }
        }
    }
}

/**
 * Create silhouette canvas from an image (HTMLImageElement).
 * Uses temp Konva Stage: black Rect + Image with destination-in and alpha filter, then toCanvas().
 * Optionally applies stroke via worker. Stroke color is always black (not configurable).
 *
 * @param {HTMLImageElement} image - Loaded image element
 * @param {number} width - Output width (display size)
 * @param {number} height - Output height (display size)
 * @param {Object} [options] - { strokeWidth, alphaThreshold } (stroke color is ignored; always black)
 * @returns {Promise<HTMLCanvasElement>} canvas with black silhouette
 */
export function createSilhouetteImage(image, width, height, options = {}) {
    const {strokeWidth = 0, alphaThreshold = FRAGMENT_DETECTION.ALPHA_THRESHOLD} = options

    return new Promise((resolve, reject) => {
        if (!image || !image.width || !image.height) {
            reject(new Error('Invalid image'))
            return
        }

        const imgW = image.width
        const imgH = image.height

        const tempStage = new Konva.Stage({
            container: document.createElement('div'),
            width: imgW,
            height: imgH,
        })

        try {
            const tempLayer = new Konva.Layer()
            tempStage.add(tempLayer)

            const tempRect = new Konva.Rect({
                width: imgW,
                height: imgH,
                fill: 'black',
                globalCompositeOperation: 'source-over',
            })

            const tempImg = new Konva.Image({
                image,
                globalCompositeOperation: 'destination-in',
                filters: [alphaFilterFactory({min: alphaThreshold, max: 255})],
            })

            tempLayer.add(tempRect)
            tempLayer.add(tempImg)
            tempImg.cache()
            tempLayer.batchDraw()

            const tempCanvas = tempStage.toCanvas({pixelRatio: 1})
            let silhouetteSource = tempCanvas

            tempStage.destroy()

            if (strokeWidth > 0) {
                const ctx = tempCanvas.getContext('2d')
                const rasterData = ctx.getImageData(0, 0, tempCanvas.width, tempCanvas.height)
                const {imageData: down, scale} = downscaleImageData(rasterData, 2000)

                ;(async () => {
                    try {
                        const worker = await getCustomShapeWorker()
                        const stroked = await worker.addStroke(down, {
                            stroke: SILHOUETTE_STROKE_BLACK,
                            strokeWidth,
                            alphaThreshold,
                        })

                        if (!stroked) {
                            resolve(silhouetteSource)
                            return
                        }

                        const imageData =
                            scale >= 1 ? stroked : resizeImageData(stroked, rasterData.width, rasterData.height)
                        const canvas = document.createElement('canvas')
                        canvas.width = silhouetteSource.width
                        canvas.height = silhouetteSource.height
                        const outCtx = canvas.getContext('2d')
                        outCtx.putImageData(imageData, 0, 0)
                        resolve(canvas)
                    } catch (err) {
                        console.error('Silhouette stroke failed:', err)
                        resolve(silhouetteSource)
                    }
                })()
            } else {
                resolve(silhouetteSource)
            }
        } catch (err) {
            tempStage.destroy()
            reject(err)
        }
    })
}
